import React, { useCallback, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import type { CSSProperties } from 'react';

import { COLORS } from 'Components/foundation';
import { IconName } from 'Components/foundation/icons';
import UTextLink from 'Components/unit/UTextLink/UTextLink';

import styles from './SIAPrompt.style';
import useTextareaHeightFitContent from './hooks/useTextareaHeightFitContent';

export type SIAPromptProps = {
  style?: CSSProperties;
  helpUrl: string;
  textValue: string | undefined;
  setTextValue: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const iconName: IconName = 'info';

export const SIAPrompt: React.FC<SIAPromptProps> = (props) => {
  const { helpUrl, textValue, setTextValue } = props;

  const strings = {
    title: t('structural_component:s_ia_prompt.title'),
    generatiorInfo: t('structural_component:s_ia_prompt.generatior_info'),
    promptPlaceholder: t('structural_component:s_ia_prompt.prompt_placeholder'),
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isMidContainerHovered, setIsMidContainerHovered] = useState<boolean>(false);

  useTextareaHeightFitContent(textareaRef);

  const computedMidContainerStyle = useMemo(() => {
    return isMidContainerHovered ? styles.middleContainer_hover : styles.middleContainer;
  }, [isMidContainerHovered]);

  const textareaOnChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTextValue(e.target?.value);
    },
    [setTextValue, textValue],
  );

  const onMouseEnterHandler = useCallback(() => {
    setIsMidContainerHovered(true);
  }, [setIsMidContainerHovered]);

  const onMouseLeaveHandler = useCallback(() => {
    setIsMidContainerHovered(false);
  }, [setIsMidContainerHovered]);

  return (
    <div style={styles.wrapper} id="s_ia_promt__wrapper">
      <div style={styles.topContainer} id="s_ia_promt__top_container">
        <h3 style={styles.title}>{strings.title}</h3>
        <UTextLink
          typography="BODY3_SEMIBOLD"
          text={strings.generatiorInfo}
          type="standard"
          leftIcon={iconName}
          link={helpUrl}
          iconSize={'medium'}
          colorOverride={COLORS.DISABLED.DEFAULT}
        />
      </div>
      <div
        id="s_ia_promt__middle_container"
        style={computedMidContainerStyle}
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
      >
        <textarea
          ref={textareaRef}
          style={styles.textArea}
          placeholder={strings.promptPlaceholder}
          onChange={textareaOnChangeHandler}
          value={textValue}
        />
      </div>
    </div>
  );
};

export default SIAPrompt;
